// Centralised booking-status + deposit-shade colour mapping.
//
// Before this module the calendar (Day + Week views) and the floor manager each
// hardcoded their own inline status→colour logic, which drifted. Both now read
// from here so a status reads the same on every surface (brief item 6 asked for
// exactly this `statusColour(status)`-style single source of truth).
//
// Two independent axes compose on a calendar tile:
//   • border / dot / label  ← bookingStatusTone(status)   (lifecycle state)
//   • fill intensity         ← depositShade(deposit)        (how much is paid)
import clsx from 'clsx';
import { lifecycleLabel } from '@atink/domain';
import type { Booking, Deposit, WaiverStatus } from '@/domain/types';
import type { DayOffKind } from '@/domain/artistAdmin';
import { depositPaidAud } from '@/utils/bookingSummary';

export interface StatusTone {
  /** Border / accent — the primary lifecycle signal on a tile. */
  border: string;
  /** Background fill used when the status (not a deposit) drives the fill. */
  fill: string;
  /** Text colour that reads against `fill`. */
  text: string;
  /** Small status dot colour for legends / badges. */
  dot: string;
  /** Human label. */
  label: string;
}

// Labels are NOT kept here: they come from @atink/domain `lifecycleLabel` so every
// surface says the same word (a solid booking is "Booked"; "Confirmed" is only the
// confirmation-call outcome — DECIDED 23 Sep, W4).
const STATUS_TONES: Record<Booking['status'], Omit<StatusTone, 'label'>> = {
  held: {
    border: 'border border-cream-faint/40',
    fill: 'bg-cream-faint/5',
    text: 'text-cream-soft',
    dot: 'bg-cream-faint',
  },
  'pending-deposit': {
    border: 'border border-gold-bright/60',
    fill: 'bg-gold/10',
    text: 'text-gold-bright',
    dot: 'bg-gold',
  },
  confirmed: {
    border: 'border border-gold-bright/70',
    fill: 'bg-gold/20',
    text: 'text-gold-bright',
    dot: 'bg-gold-bright',
  },
  'checked-in': {
    border: 'border border-azure/70',
    // A touch stronger than the other status fills so a checked-in tile reads as
    // a clearly blue block, not a near-transparent wash over the calendar.
    fill: 'bg-azure/25',
    text: 'text-azure',
    dot: 'bg-azure',
  },
  'in-chair': {
    border: 'border border-violet/70',
    fill: 'bg-violet/10',
    text: 'text-violet',
    dot: 'bg-violet',
  },
  rescheduled: {
    border: 'border border-dashed border-gold-dim/70',
    fill: 'bg-gold-dim/10',
    text: 'text-gold-dim',
    dot: 'bg-gold-dim',
  },
  completed: {
    border: 'border border-mint/60',
    fill: 'bg-mint/10',
    text: 'text-mint',
    dot: 'bg-mint',
  },
  cancelled: {
    border: 'border border-cream-faint/30',
    fill: 'bg-cream-faint/5',
    text: 'text-cream-faint line-through',
    dot: 'bg-cream-faint',
  },
  'no-show': {
    border: 'border border-blood/40',
    fill: 'bg-blood/5',
    text: 'text-blood/70',
    dot: 'bg-blood/60',
  },
  // The kept-original record after a reschedule: a faded, struck ghost. It is
  // history, not a live slot, so it reads quietly and never claims occupancy.
  superseded: {
    border: 'border border-dashed border-cream-faint/30',
    fill: 'bg-cream-faint/5',
    text: 'text-cream-faint line-through',
    dot: 'bg-cream-faint/50',
  },
};

export function bookingStatusTone(status: Booking['status']): StatusTone {
  return { ...STATUS_TONES[status], label: lifecycleLabel({ status }) };
}

// Waiver lifecycle colours, reusing the shared status vocabulary: mint = signed,
// gold = sent/awaiting signature, blood = expired, cream-faint = not-sent.
const WAIVER_TONES: Record<WaiverStatus, StatusTone> = {
  'not-sent': {
    border: 'border border-cream-faint/40',
    fill: 'bg-cream-faint/5',
    text: 'text-cream-soft',
    dot: 'bg-cream-faint',
    label: 'Waiver not sent',
  },
  sent: {
    border: 'border border-gold-bright/60',
    fill: 'bg-gold/10',
    text: 'text-gold-bright',
    dot: 'bg-gold-bright',
    label: 'Waiver sent',
  },
  signed: {
    border: 'border border-mint/60',
    fill: 'bg-mint/10',
    text: 'text-mint',
    dot: 'bg-mint',
    label: 'Waiver signed',
  },
  expired: {
    border: 'border border-blood/50',
    fill: 'bg-blood/10',
    text: 'text-blood',
    dot: 'bg-blood',
    label: 'Waiver expired',
  },
};

export function waiverTone(status: WaiverStatus): StatusTone {
  return WAIVER_TONES[status];
}

export type DepositLevel = 'full' | 'partial' | 'none';

/**
 * How much of a deposit has been paid: the ledger's money in vs `amountAud`.
 * A `paid` deposit with no explicit `amountPaid` is treated as fully paid so
 * existing seeds don't need backfilling.
 */
export function depositPaidLevel(deposit: Deposit | undefined): DepositLevel {
  if (!deposit) return 'none';
  // calendar-12 (b): the money actually on it — the ledger nets top-ups, rollovers
  // and forfeits — not the amountPaid field alone.
  const paid = depositPaidAud(deposit);
  if (paid <= 0) return 'none';
  if (paid >= deposit.amountAud) return 'full';
  return 'partial';
}

export interface DepositShade {
  level: DepositLevel;
  /** Background fill class (the three calendar shades). */
  fill: string;
  /** Text colour that reads against `fill`. */
  text: string;
  label: string;
}

const SHADES: Record<DepositLevel, Omit<DepositShade, 'level'>> = {
  full: { fill: 'bg-gold-bright/90', text: 'text-ink-black', label: 'Paid in full' },
  partial: { fill: 'bg-gold-bright/40', text: 'text-gold-bright', label: 'Part-paid' },
  none: { fill: 'bg-gold-bright/10', text: 'text-gold-bright', label: 'Awaiting deposit' },
};

export function depositShade(deposit: Deposit | undefined): DepositShade {
  const level = depositPaidLevel(deposit);
  return { level, ...SHADES[level] };
}

/**
 * Combined tile treatment for the calendar: border/dot/label from the booking
 * status, fill from the deposit shade for deposit-relevant (upcoming) statuses,
 * else from the status itself (in-chair / completed / cancelled / no-show).
 */
export function bookingTileTone(
  booking: Booking,
  deposit: Deposit | undefined,
): { className: string; status: StatusTone; shade: DepositShade } {
  const status = bookingStatusTone(booking.status);
  const shade = depositShade(deposit);
  const depositDriven =
    booking.status === 'held' ||
    booking.status === 'pending-deposit' ||
    booking.status === 'confirmed' ||
    booking.status === 'rescheduled';
  return {
    className: clsx(status.border, depositDriven ? [shade.fill, shade.text] : [status.fill, status.text]),
    status,
    shade,
  };
}

export interface DayOffTone {
  /** Border/accent for the full-day placeholder tile. */
  border: string;
  /** Muted background fill. */
  fill: string;
  /** Text colour that reads against `fill`. */
  text: string;
  /** Short human label. */
  label: string;
}

const DAY_OFF_TONES: Record<DayOffKind, DayOffTone> = {
  rdo: {
    border: 'border border-dashed border-cream-faint/40',
    fill: 'bg-cream-faint/5',
    text: 'text-cream-soft',
    label: 'RDO',
  },
  comp: {
    border: 'border border-dashed border-azure/50',
    fill: 'bg-azure/10',
    text: 'text-azure',
    label: 'Comp day',
  },
  leave: {
    border: 'border border-dashed border-gold-dim/50',
    fill: 'bg-gold-dim/10',
    text: 'text-gold-dim',
    label: 'Leave',
  },
};

export function dayOffTileTone(kind: DayOffKind): DayOffTone {
  return DAY_OFF_TONES[kind];
}
